import React from 'react'
import { Helmet } from 'react-helmet'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import { adminUrl, appUrl } from 'config/constants/urls'
import Account from 'src/apps/Account'
import AccountInvitation from 'src/apps/Account/apps/Invitation'
import AdminWorkspace from 'src/apps/AdminWorkspace'
import Bookmarks from 'src/apps/Bookmarks'
import Home from 'src/apps/Home'
import Initiative from 'src/apps/Initiative'
import Messages from 'src/apps/Messages'
import Notebook from 'src/apps/Notebook'
import Page from 'src/apps/Page'
import Profile from 'src/apps/Profile'
import Search from 'src/apps/Search'
import Start from 'src/apps/Start'
import ModalRoute from 'src/components/utils/ModalRoute'
import { useGoogleAnalytics } from 'src/components/utils'
import { useMe, useOrganization } from 'src/graphql/hooks'
import { ThemeProvider } from 'src/libraries/contexts/Theme'

// App components
import Blog from 'src/apps/Blog'
import Discussions from 'src/apps/Discussions'
import Events from 'src/apps/Events'
import Groups from 'src/apps/Groups'
import Resources from 'src/apps/Resources'

// Note to the curious:
// We automagically statically swap DevTools with NotFound in production, so that
// it doesn't bloat the build bundle. See production webpack config for details.
import DevTools from 'src/apps/DevTools'

// Global components
import NotFound from '~/apps/NotFound'
import CookieBanner from '~/components/CookieBanner'
import GlobalLayout from '~/components/Layout'

// Lazy Loaded Components
const Explore = React.lazy(() => import('src/legacy/5.6/Explore'))
const OpsCentre = React.lazy(() => import('src/apps/Ops'))

// =======================================================
// App components
// =======================================================

const appTypeComponentMap = {
   blog: Blog,
   discussions: Discussions,
   events: Events,
   groups: Groups,
   resources: Resources
}

// =======================================================
// Secret Community Utils
// =======================================================

const allowedPathsInSecretCommunity = ['/account', '/invitation', '/login', '/dev']
const pathIsAllowedInSecretCommunity = pathname => (
   allowedPathsInSecretCommunity.some(allowedPath => pathname.startsWith(allowedPath))
)

// =======================================================
// Component
// =======================================================

const GlobalApp = () => {
   const { organization } = useOrganization()
   const location = useLocation()
   const { me, isLoggedIn, loading: userIsLoading } = useMe()
   useGoogleAnalytics()

   // If the community is a secret one, then restrict the paths to which the current
   // user is allowed to accesss if they are not logged in.
   if (organization.privacy === 'secret' && !pathIsAllowedInSecretCommunity(location.pathname)) {
      if (userIsLoading) {
         return null
      }

      if (!isLoggedIn) {
         return (
            <Redirect to="/account/login" />
         )
      }
   }

   const defaultFaviconUrl = '/assets/images/defaultFavicon.png'
   const logoFaviconUrl = organization?.logoFavicon?.file.thumbnailUrlW240 || defaultFaviconUrl

   return (
      <ThemeProvider>
         <Helmet
            defaultTitle={organization.name}
            link={[{
               rel: 'icon',
               type: 'image/png',
               href: logoFaviconUrl
            }]}
         />
         <GlobalLayout>
            <Switch>
               <ModalRoute path={adminUrl}>
                  {({ open, onCloseComplete, onCloseTrigger }) => (
                     <>
                        <Home />
                        <AdminWorkspace
                           modalOnCloseComplete={onCloseComplete}
                           modalOnCloseTrigger={onCloseTrigger}
                           modalOpen={open}
                        />
                     </>
                  )}
               </ModalRoute>
               <Route path="/" exact component={Home} />
               <Route path="/home" component={Home} />
               <Redirect from="/login" to="/account/login" />
               <Route path="/account" component={Account} />
               <Route path="/bookmarks" component={Bookmarks} />
               <Route path="/initiatives/:initiativeSlug" component={Initiative} />
               <Route path="/invitation" component={AccountInvitation} />
               <Route path="/messages" component={Messages} />

               <Route path="/notebook/:notebookSlug" component={Notebook} />

               {/* Page Route */}
               <Route path="/pages/:pageSlug" component={Page} />

               {/* Profile Routes */}
               <Redirect from="/profile" exact to="/profile/me" />
               {isLoggedIn && <Redirect from={`/profile/${me.id}`} exact to="/profile/me" />}
               <Route path="/profile/:userId" component={Profile} />

               <Route path="/search" component={Search} />
               <Route path="/start" component={Start} />

               {/* App Routes */}
               {organization.apps.filter(a => a.enabled).map(app => (
                  <Route
                     key={app.id}
                     path={appUrl(app)}
                     component={appTypeComponentMap[app.type]}
                  />
               ))}

               {organization.globalUrls.map(globalUrl => (
                  <Route key={globalUrl.url} path={`/${globalUrl.url}`} render={() => {
                     if (globalUrl.pageId && !globalUrl.initiativeId) {
                        return <Page />
                     }

                     // TODO: Not the most elegant, since this does a redirect to
                     // the normal initiative URL
                     // See: #657
                     return <Initiative />
                  }} />
               ))}

               {/* We put the Explore route below custom pages so that we can
                  override the Explore page. Eventually this route will be deprecated anyway */}
               <Route path="/explore" render={() => (
                  <React.Suspense fallback={null}>
                     <Explore />
                  </React.Suspense>
               )} />

               {/* Operational Tools Limited to Mother Users */}
               <Route path="/ops" render={() => (
                  <React.Suspense fallback={null}>
                     <OpsCentre />
                  </React.Suspense>
               )} />

               {/* We automatically statically replace DevTools with NotFound
                   in production builds */}
               <Route path="/dev" component={DevTools} />
               <Route component={NotFound} />
            </Switch>
         </GlobalLayout>
         {/* TODO: This should be made generic
         See PR #360 */}
         {organization.slug === 'gns3' && <CookieBanner />}
      </ThemeProvider>
   )
}

export default GlobalApp
